import clsx from "clsx"
import { forwardRef } from "react"

import debounce from "@utils/debounce"
import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Icon } from "@atoms"
import buttonStyles from "@atoms/Button/Button.module.scss"

import styles from "./IconButton.module.scss"
import { IconButtonProps } from "./IconButton.types"

function IconButton(props: IconButtonProps, ref: React.Ref<HTMLButtonElement>) {
    const {
        size,
        variant,
        type = "button",
        className,

        isTabbable = true,

        icon,
        iconOnActive = undefined,
        iconIsActive = false,
        iconClassName,
        iconRotation = undefined,

        onClick,
        noDebounce,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isProcessing = false,
        isDisabled = false,
        isLoading = false,

        ...rest
    } = props

    const iconSize = 14

    return (
        <button
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isProcessing]: isProcessing,
                    [styles.isDisabled]: isDisabled,
                    [styles.isLoading]: isLoading,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                },
                styles[`${variant}Variant`],
                styles[`${size}Size`],
            )}
            type={type}
            onClick={
                noDebounce
                    ? onClick
                    : debounce((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClick?.(event))
            }
            disabled={isDisabled || isProcessing || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isProcessing}
            aria-pressed={isPressed}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            ref={ref}
            {...rest}
        >
            {isLoading ? (
                <div className={styles.skeleton}>
                    <Skeleton width={iconSize} height={iconSize} circle={true} />
                </div>
            ) : (
                <div className={clsx({ [buttonStyles.iconHidden]: isProcessing })}>
                    <Icon
                        icon={icon}
                        iconOnActive={iconOnActive}
                        isActive={iconIsActive}
                        size={iconSize}
                        className={iconClassName}
                        rotation={iconRotation}
                        container="fixedHeight"
                    />
                </div>
            )}
            {isProcessing && (
                <div className={buttonStyles.spinnerWrapper}>
                    <Icon
                        icon={faIcon({ name: "spinner-third", style: "regular" })}
                        spin={true}
                        size={iconSize}
                        container="fixedHeight"
                    />
                </div>
            )}
        </button>
    )
}

export default forwardRef(IconButton)
